import React, { useEffect, useMemo, useState } from 'react';
import styled from 'astroturf/react';

import {
  recordShow,
  recordDismissal,
  recordClickthrough,
} from 'js/utils/dismissibles';
import { trackClick, Options as TrackClickOptions } from 'js/utils/trackClick';

import { Body } from './Body';
import { Button } from './Button';
import { Heading } from './Heading';
import { Icon } from './Icon';

const DefaultContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-xs-16, 16px);
  padding: var(--spacing-xs-16, 16px) var(--spacing-lg-64, 64px);

  @include breakpoint(small) {
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    padding: var(--spacing-xs-16, 16px) var(--spacing-sm-24, 24px);
    gap: var(--spacing-sm-24, 24px);
  }

  background-color: var(--background-black, #141413);
  color: var(--text-white, #ffffff);
`;

const AlertContainer = styled(DefaultContainer)`
  background-color: var(--error-light, #fff2f2);
  color: var(--text-error, #b60000);
`;

const BeigeContainer = styled(DefaultContainer)`
  background-color: var(--brand-colors-beige-200, #f2f0ec);
  color: var(--text-beige-primary, #3a372f);
`;

const BannerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-16, 16px);

  @include breakpoint(small) {
    gap: 10px;
  }
`;

const TextContainer = styled.div`
  padding-right: var(--spacing-xs-8, 8px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--spacing-xs-4, 4px);
  flex: 1 0 0;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-8, 8px);
`;

const ContentContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 0 0;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: var(--spacing-xs-16, 16px);

  @include breakpoint(small) {
    width: 100%;
    flex-direction: column;
  }
`;

const ActionButton = styled(Button)`
  white-space: nowrap;

  @include breakpoint(small) {
    width: 100%;
  }
`;

const CloseButton = styled.div`
  position: absolute;
  top: 0;
  right: 0;

  width: 36px;
  height: 36px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: inherit;
  cursor: pointer;
  opacity: 0.8;

  &:hover {
    opacity: 1;
  }
`;

type UnibrowVariant = 'default' | 'alert' | 'beige';

type UnibrowButton = {
  text: string;
  onClick: () => void;
  icon?: string | React.ReactNode;
  iconRight?: string | React.ReactNode;
  trackClick?: TrackClickOptions;
};

export type UnibrowProps = {
  dismissibleName: string;
  variant?: UnibrowVariant;
  icon?: string | React.ReactNode;
  text: string;
  content?: React.ReactNode;
  eyebrowIcon?: string | React.ReactNode;
  eyebrowText?: string;
  primaryButton?: UnibrowButton;
  secondaryButton?: UnibrowButton;
};

/**
 * A full-width banner that appears at the top of the page above the navbar, for uses
 * such as alerts, reminders, announcements, upsells, etc. Automatically tracked displays
 * and clicks to the `dismissibles` table, using the given `dismissibleName`.
 *
 * Props:
 * - `dismissibleName`: Unique identifier for tracking dismissal state (required)
 * - `variant`: Visual style variant - 'default' (dark), 'alert' (red), or 'beige'
 * - `icon`: The name of a Material Symbols icon (string) or an arbitrary React component to display
 *     as the banner's main icon (left of text)
 * - `text`: Main heading text for the banner (required)
 * - `content`: Optional React node to display as additional content in the center of the banner
 * - `eyebrowIcon`: The name of a Material Symbols icon (string) or React component for the eyebrow
 * - `eyebrowText`: Small text label displayed above the main heading
 * - `primaryButton`: Primary action button configuration with text, onClick handler, and optional icons and click tracking options
 * - `secondaryButton`: Secondary action button configuration with text, onClick handler, and optional icons and click tracking options
 *
 * ## Basic Usage
 *
 * ```tsx
 * <Unibrow
 *   dismissibleName="feature-announcement"
 *   text="New features available!"
 *   primaryButton={{
 *     text: "Learn More",
 *     onClick: () => window.location.href = "/features"
 *   }}
 * />
 * ```
 *
 * ## Eyebrow + Alert Variant
 *
 * ```tsx
 * <Unibrow
 *   dismissibleName="payment-alert"
 *   variant="alert"
 *   icon="warning"
 *   eyebrowText="Payment Problem"
 *   text="Your payment was declined — update now to keep your Premium access"
 *   primaryButton={{
 *     text: "Update Payment",
 *     onClick: () => window.location.href = "/settings/payment"
 *   }}
 * />
 * ```
 */
export default function Unibrow({
  dismissibleName,
  variant = 'default',
  icon,
  text,
  content,
  eyebrowIcon,
  eyebrowText,
  primaryButton,
  secondaryButton,
}: UnibrowProps) {
  const [show, setShow] = useState(true);

  useEffect(() => {
    recordShow(dismissibleName);
  }, [dismissibleName]);

  const handleButtonClick = ({
    onClick,
    trackClick: trackClickOptions,
  }: UnibrowButton) => {
    recordClickthrough(dismissibleName);
    if (trackClickOptions) {
      trackClick(trackClickOptions);
    }
    onClick();
    setShow(false);
  };

  const handleClose = () => {
    recordDismissal(dismissibleName);
    setShow(false);
  };

  const Container = useMemo(() => {
    switch (variant) {
      case 'alert':
        return AlertContainer;
      case 'beige':
        return BeigeContainer;
      default:
        return DefaultContainer;
    }
  }, [variant]);

  if (!show) return null;

  return (
    <Container>
      <BannerContainer>
        {icon &&
          (typeof icon === 'string' ? (
            <IconContainer>
              <Icon name={icon} size={32} />
            </IconContainer>
          ) : (
            <IconContainer>{icon}</IconContainer>
          ))}
        <TextContainer>
          <TextBlock>
            {eyebrowText && (
              <Eyebrow>
                {eyebrowIcon && (
                  <IconContainer>
                    {typeof eyebrowIcon === 'string' ? (
                      <Icon name={eyebrowIcon} size={16} />
                    ) : (
                      eyebrowIcon
                    )}
                  </IconContainer>
                )}
                <Body size="sm" bold>
                  {eyebrowText}
                </Body>
              </Eyebrow>
            )}
            <Heading size="sm" fontFamily="sans-serif">
              {text}
            </Heading>
          </TextBlock>
        </TextContainer>
      </BannerContainer>
      {content && <ContentContainer>{content}</ContentContainer>}
      {(primaryButton || secondaryButton) && (
        <ButtonContainer>
          {primaryButton && (
            <ActionButton
              inverse={variant === 'default'}
              size="sm"
              icon={primaryButton.icon}
              iconRight={primaryButton.iconRight}
              onClick={() => handleButtonClick(primaryButton)}
            >
              {primaryButton.text}
            </ActionButton>
          )}
          {secondaryButton && (
            <ActionButton
              inverse={variant === 'default'}
              size="sm"
              secondary
              icon={secondaryButton.icon}
              iconRight={secondaryButton.iconRight}
              onClick={() => handleButtonClick(secondaryButton)}
            >
              {secondaryButton.text}
            </ActionButton>
          )}
        </ButtonContainer>
      )}
      <CloseButton
        onClick={handleClose}
        role="button"
        aria-label="Close"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClose();
          }
        }}
      >
        <Icon name="close" />
      </CloseButton>
    </Container>
  );
}
